import { ComponentProps } from 'lib/component-props';
import useHelpers from 'hooks/helpers';
import { useI18n } from 'next-localization';
import ArticleCardHorizontal from 'components/Tiles/ArticleCardHorizontal';
import ArticleCard from 'components/Tiles/ArticleCard';
import styles from './styles/SeriesArticles.module.scss';
import { GetStaticComponentProps, useComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';
import { defaultLanguage } from 'temp/config';
import { seriesArticlesService } from 'lib/services/SeriesArticles';
import { ArticleData, slugToName } from 'model/Article';

type SeriesArticlesProps = ComponentProps & {
  baseUrl: {
    value: string;
  };
};

const MIN_LENGTH_TO_DISPLAY_ARTICLE = 1;

const SeriesArticles = ({ rendering, fields }: SeriesArticlesProps) => {
  const { anchorId } = useHelpers(rendering);
  const { t } = useI18n();
  const articles = useComponentProps<ArticleData[]>(rendering.uid);

  const getArticleCardFields = (article) => {
    return {
      ...article,
      articleType: article.template.id,
      strapline: article.articleStrapline.value,
      publicationDate: article.articlePublicationDate.formattedDateValue,
      tag: article.articleCategory.targetItem.name_1e4c2734dc754d528af8084e6c400e1f.value,
      baseUrl: fields.baseUrl.value,
      articleImage: article.articleImage.src,
      articleImageAlt: article.articleImage.alt,
    };
  };

  return (
    <section id={anchorId} className="default-section-padding background-white">
      <div className="container">
        <h2 className="h2">{t('seriesTotal').replace('{token.total}', `${articles?.length}`)}</h2>

        {articles?.length > MIN_LENGTH_TO_DISPLAY_ARTICLE && (
          <div className="mt-5">
            <h3 className="h3">{t('seriesLatest')}</h3>

            <div className="mt-3">
              <div className={styles['latest-article-wrapper']}>
                <ArticleCardHorizontal data={getArticleCardFields(articles[0])} />
              </div>
            </div>
          </div>
        )}

        <div className="mt-5">
          <h3 className="h3">{t('seriesMoreFrom')}</h3>

          <div className="row mt-5">
            {articles?.map(
              (article, index) =>
                (articles?.length <= MIN_LENGTH_TO_DISPLAY_ARTICLE || index !== 0) && (
                  <div key={article.url} className="col-12 col-md-6 col-lg-3 mb-4">
                    <ArticleCard
                      fields={getArticleCardFields(article)}
                      cardCategoryField="tag"
                      useLineClamps={false}
                    />
                  </div>
                )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData, context) => {
  const seriesName = slugToName(context.params?.path?.at(-1));
  const siteId = layoutData.sitecore.context?.siteRoot as string;
  const language: string = layoutData.sitecore.context.language ?? defaultLanguage;
  const dataBySeriesName = await seriesArticlesService.getSeriesBanner(language, seriesName);

  if (dataBySeriesName.length !== 0) {
    const articles = await seriesArticlesService.getSeriesArticles(
      language,
      siteId,
      dataBySeriesName?.id
    );

    return articles;
  } else {
    return {
      notFound: true,
    };
  }
};

export default SeriesArticles;
